<template>
  <div class="pa-4" style="position: relative; height: 100%;">
    <debt-amtz-dialog ref="debtAmtzDialog" />

    <active-plan 
      :household="household" 
      :plan="planData"
      :plans="plans"
      @on-open-min-debt="$refs.debtAmtzDialog.open($event, true)"
      @on-open-plan-debt="$refs.debtAmtzDialog.open($event)"
      @on-new-plan="$emit('on-new-plan', $event)"
      @on-debt-updated="$emit('on-debt-updated', $event)"
      @on-policy-updated="$emit('on-policy-updated', $event)"
      @on-plan-updated="$emit('on-plan-updated', $event)"
      @on-plan-status-updated="$emit('on-plan-status-updated', $event)"
      v-if="showWiqPlan" 
    />
    <min-plan 
      :household="household" 
      :plan="planData" 
      @on-open-min-debt="$refs.debtAmtzDialog.open($event, true)"
      @on-update-intake="$emit('on-update-intake', $event)"
      @on-policy-updated="$emit('on-policy-updated', $event)"
      @on-new-plan="$emit('on-plan-updated', $event)"
      v-else-if="showMinPlan" 
    />

    <v-row v-else style="height: 100%;" class="align-start">
      <v-col cols="12" class="text-center">
        <div class="display-1 mt-8">Welcome to Freedom Accelerator!</div>
        <div class="subtitle-1 my-4">Click below to start saving money</div>
        <v-btn x-large color="primary" class="mt-4" :to="{name: 'households-prd-freedom-accelerator-new'}"><v-icon x-large class="mr-2">mdi-finance</v-icon>Create A Plan</v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import DebtAmtzDialog from './DebtAmtzDialog'
import ActivePlan from './ActivePlan'
import MinPlan from './MinPlan'

export default {
  name: 'FreedomAcceleratorIndex',
  props: {
    household: {
      type: Object,
      required: true
    },
    plan: {
      type: Object
    },
    plans: {
      type: Array
    }
  },
  components: {
    DebtAmtzDialog,
    ActivePlan,
    MinPlan
  },
  data () {
    return {
      planData: {}
    }
  },
  mounted() {
    this.planData = (this.plan ? JSON.parse(JSON.stringify(this.plan)) : {})
  },
  watch: {
    plan: {
      handler() {
        this.planData = (this.plan ? JSON.parse(JSON.stringify(this.plan)) : {})
      },
      deep: true
    }
  },
  computed: {
    showWiqPlan() {
      return this.planData && this.planData.data && this.planData.algorithm != 'min_only'
    },
    showMinPlan() {
      return this.planData && this.planData.data && this.planData.algorithm == 'min_only'
    },
    minTime() {
      return this.summaryTime(this.planData.min_freedom_months)
    },
    debtFreeTime() {
      return this.summaryTime(this.planData.freedom_months)
    },
    policyReplenishedTime() {
      return this.summaryTime(this.planData.payoff_months)
    },
    monthlyCapital() {
      let total = 0
      if(this.planData.debts) {
        Object.values(this.planData.debts).forEach(d => {
          total += parseFloat(d.minimum_payment)
        })
      }
      if(this.planData.policies) {
        Object.values(this.planData.policies).forEach(p => {
          if(!p.annual && p.premium) total += parseFloat(p.premium)
        })
      }
      return total
    },
    yearlyCapital() {
      let total = 0
      if(this.planData.policies) {
        Object.values(this.planData.policies).forEach(p => {
          if(p.annual && p.premium) total += parseFloat(p.premium)
        })
      }
      return total
    },
    totalYearly() {
      const monthly = this.monthlyCapital || 0
      const yearly = this.yearlyCapital || 0
      return (monthly * 12) + yearly
    },
    interestVolume() {
      return Math.round(this.planData.total_min_interest / this.planData.total_debt_principal * 100)
    },
    planInterestVolume() {
      return Math.round(this.planData.total_interest / this.planData.total_debt_principal * 100)
    },
    year70() {
      if(!this.planData.yearly_summary) return null

      let ref = this
      let ageKey = null
      Object.keys(this.planData.yearly_summary).forEach(yk => {
        const summary = ref.planData.yearly_summary[yk]
        Object.keys(summary.policies).forEach(pk => {
          const policy = summary.policies[pk]
          console.log(policy.age)
          if(policy.age == 70) ageKey = yk
        })
      })
      return ageKey
    },
    at70() {
      if(!this.planData.yearly_summary) return null

      let deathBen  = 0
      let cashVal   = 0
      let savings   = 0

      let key         = this.year70
      if(key === null) return {}

      const summary     = this.planData.yearly_summary[ key ]
      savings         = summary.savings
      Object.keys(summary.policies).forEach(pk => {
        const policy = summary.policies[pk]
        deathBen  += policy.death_ben_end
        cashVal   += policy.cash_val_end
      })
      return {
        deathBenefit: deathBen,
        cashValue: cashVal,
        savings: savings
      }
    }
  },
  methods: {
    summaryTime(months) {
      if(isNaN(months)) return '-'
      if(months < 12) {
        return months + (months !== 1 && 's')
      }
      const years = Math.round(months / 12 * 10) / 10
      return years + ' Year' + (years !== 1 && 's')
    }
  }
}
</script>

<style lang="scss" scoped>

  .faSummaryToggle {
    cursor: pointer;
    &:hover {
      background-color: rgba(0,0,0,.1) !important;
    }
  }
  .faCompareToggle {
    cursor: pointer;
    &:hover {
      background-color: rgba(0,0,0,.05);
    }
  }
  .faSummaryCell {
    border-top: thin solid rgba(0,0,0,.05);
    padding: 4px 6px;
    &:first-of-type {
      border-top: none;
    }

    ::v-deep .faSummaryTitle {
      font-size: 14px;
      font-weight: 400;
    }

    ::v-deep .faSummaryDesc {
      font-size: 14px;
      font-weight: 400;
    }
  }

  .comparisonContainer {
    >div {
      border-top: thin solid rgba(0,0,0,.1);
      &:first-of-type {
        border-top: none;
      }
    }
  }
  .faButton {
    height: 160px;
    width: 200px;
    max-width: 200px;
    min-width: 200px;
    >.faButtonContent {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      display: flex;
      flex-direction: column;
    }
  }
</style>
